import {Component, computed, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatInput, MatPrefix} from '@angular/material/input';
import {Company} from '../classes/company';
import {CodaltComponent} from '../codalt.component';
import {CompanyService} from '../services/company.service';
import {DataListComponent} from '../shared/ui/data-list/data-list.component';
import {DataRowComponent} from '../shared/ui/data-list/data-row.component';
import {EmptyStateComponent} from '../shared/ui/empty-state/empty-state.component';
import {FilterChipsComponent, FilterOption} from '../shared/ui/filter-chips/filter-chips.component';
import {PageHeaderComponent} from '../shared/ui/page-header/page-header.component';
import {PageComponent} from '../shared/ui/page/page.component';
import {StatusBadgeComponent} from '../shared/ui/status-badge/status-badge.component';

/** Overview chips; filtering is for display only. */
type CompanyFilter = 'alle' | 'groundbirds' | 'intern' | 'metAzure' | 'zonderAfas';

const FILTERS: Record<CompanyFilter, (company: Company) => boolean> = {
    alle: () => true,
    groundbirds: company => !!company.is_groundbirds,
    intern: company => !!company.is_internal,
    metAzure: company => !!company.azure_desc,
    zonderAfas: company => !company.afas_debtor_id,
};

@Component({
    selector: 'app-companies-list',
    templateUrl: './companies.component.html',
    styleUrls: ['./companies.component.scss'],
    imports: [
        RouterLink,
        MatButton,
        MatFormField,
        MatInput,
        MatPrefix,
        DataListComponent,
        DataRowComponent,
        EmptyStateComponent,
        FilterChipsComponent,
        PageComponent,
        PageHeaderComponent,
        StatusBadgeComponent,
    ]
})
export class CompaniesComponent extends CodaltComponent implements OnInit {
    readonly companies = signal<Company[]>(null);
    readonly query = signal('');
    readonly filter = signal<CompanyFilter>('alle');
    showCreate: boolean = false;

    private readonly searched = computed(() => {
        const query = this.query().trim().toLowerCase();
        return (this.companies() ?? []).filter(company => !query ||
            [company.name, company.azure_desc, company.afas_debtor_id].filter(Boolean).join(' ').toLowerCase().includes(query));
    });

    readonly rows = computed(() => this.searched().filter(FILTERS[this.filter()]));

    readonly filters = computed<FilterOption<CompanyFilter>[]>(() => {
        const companies = this.searched();
        const option = (value: CompanyFilter, label: string): FilterOption<CompanyFilter> =>
            ({value, label, count: companies.filter(FILTERS[value]).length});
        return [
            option('alle', 'Alle'),
            option('groundbirds', 'Groundbirds'),
            option('intern', 'Intern'),
            option('metAzure', 'Met Azure-team'),
            option('zonderAfas', 'Zonder AFAS-debiteur'),
        ];
    });

    constructor(
        private companyService: CompanyService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.companyService.getCompanies().subscribe((companies) => {
                this.companies.set(companies);
            })
        );
    }
}
